/**
 * Full-page plan editor route (spec 2026-08-22-02 §1).
 *
 * English route segment, like every other route in this app; the French wording
 * lives in the i18n bundle only. `?calibrate=<registrationUid>` deep-links into
 * the editor's calibrate mode (spec 2026-08-22-03 §6).
 */
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router'
import { $api } from '../../../../api/queryClient'
import { PlanEditorPage } from '../../../../components/plan-editor/PlanEditorPage'
import { PLAN_SCENE_PATH } from '../../../../components/plan-editor/useSavePlan'
import { lotBaseTypeCode } from '../../../../lib/asset-type-tree'

interface PlanEditorSearch {
    calibrate?: string
    /** `?focus=<assetUid>` — open on that asset, selected and centred. */
    focus?: string
    /**
     * `?center=<x>,<y>&zoom=<px per mm>` — the viewport itself, so a link
     * reopens on the same part of the plan at the same magnification.
     */
    center?: string
    zoom?: number
}

/** `"8000,6500"` → the millimetre point, or undefined if it is not one. */
function parseCenter(raw: unknown): string | undefined {
    if (typeof raw !== 'string') return undefined
    const parts = raw.split(',')
    if (parts.length !== 2) return undefined
    // `Number('')` is 0, which is finite — an empty half is not a coordinate.
    return parts.every((p) => p.trim() !== '' && Number.isFinite(Number(p))) ? raw : undefined
}

export const Route = createFileRoute('/$org/assets/$assetUid_/plan-editor')({
    validateSearch: (search: Record<string, unknown>): PlanEditorSearch => {
        const zoom = Number(search.zoom)
        return {
            calibrate: typeof search.calibrate === 'string' && search.calibrate ? search.calibrate : undefined,
            focus: typeof search.focus === 'string' && search.focus ? search.focus : undefined,
            center: parseCenter(search.center),
            // A non-positive scale is not a view, it is a division by zero
            // waiting to happen in every screen↔millimetre conversion.
            zoom: Number.isFinite(zoom) && zoom > 0 ? zoom : undefined,
        }
    },
    component: PlanEditorRoute,
})

function PlanEditorRoute() {
    const { org, assetUid } = useParams({ from: '/$org/assets/$assetUid_/plan-editor' })
    const { calibrate, focus, center, zoom } = useSearch({ from: '/$org/assets/$assetUid_/plan-editor' })
    // A lot whose own frame holds nothing is drawn on its floor's: the rooms,
    // the scan and the calibration live there. Editing from the lot opens that
    // frame, framed on the lot, the same answer its Plan tab already shows.
    // A lot with content of its own keeps its own frame.
    const sceneQuery = $api.useQuery('get', PLAN_SCENE_PATH, {
        params: { path: { orgCode: org, assetUid }, query: { context: 'asset' as const } },
    })
    if (sceneQuery.isPending) return null
    const borrowed = borrowedFloorFrame(sceneQuery.data)
    return (
        <PlanEditorPage
            key={assetUid}
            org={org}
            assetUid={borrowed?.frameUid ?? assetUid}
            subject={borrowed?.subject}
            calibrateRegistrationUid={calibrate}
            focusAssetUid={focus ?? borrowed?.subject.uid}
            center={center}
            zoom={zoom}
        />
    )
}

/**
 * The floor frame a lot's scene was borrowed from, or nothing when the asset is
 * edited in its own frame. Only a lot borrows, and only from a frame the editor
 * can anchor: a floor whose plan is empty must not open its building's.
 */
function borrowedFloorFrame(scene: { frame_asset: { uid: string; type_code?: string }; subject_asset?: { uid: string; name?: string; type_code?: string } } | undefined) {
    const subject = scene?.subject_asset
    if (!scene || !subject?.uid || lotBaseTypeCode(subject.type_code) !== 'unit') return undefined
    if (!['floor', 'unit'].includes(lotBaseTypeCode(scene.frame_asset.type_code))) return undefined
    return { frameUid: scene.frame_asset.uid, subject: { uid: subject.uid, name: subject.name ?? subject.uid } }
}
